import {
    Component,
    ElementRef,
    Input,
    OnInit,
    AfterViewInit,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    ChangeDetectorRef,
} from '@angular/core';
import { WellHealthinessService } from 'app/modules/well-healthiness/services/well-healthiness.service';
import { ProductionStatusCategoryResponse } from 'app/modules/well-healthiness/model';
import * as am4core from '@amcharts/amcharts4/core';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { DatatableComponent, ColumnMode } from '@swimlane/ngx-datatable';
import { ngxDatatable } from 'app/core/shared/helper/datatable.helper';
import { NgxDatatable } from 'app/model';
import module from 'app/modules/well-healthiness/well-healthiness.module';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'app-well-prod-category',
    templateUrl: './prod-category.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./prod-category.component.scss'],
})
export class ProdCategoryComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() data: ProductionStatusCategoryResponse;
    @ViewChild('prodPageContainer', { static: true }) parentElement: ElementRef;
    @ViewChild('ngxDatatable', { static: true }) element: DatatableComponent;
    ngx: NgxDatatable<any>;
    private transformedData: any[] = [];

    constructor(
        public service: WellHealthinessService,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly router: Router,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        // Initialize dt structure directly without ngxDatatable helper
        this.ngx = {
            dt: {
                rows: [],
                column: this.setDataTable().column,
                page: {
                    size: 10,
                    totalRecords: 0,
                    totalPages: 0,
                    offset: 0,
                },
            },
            searchForm: null,
            onPage: null,
            element: this.element,
            parentElement: this.parentElement,
            onInit: () => {},
            onDestroy: () => {},
        } as any;

        this.transformProductionStatusData();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] && this.data?.data?.items) {
            this.transformProductionStatusData();
            this.cdr.markForCheck();
        }
    }

    transformProductionStatusData() {
        if (!this.data?.data?.items) {
            this.transformedData = [];
            return;
        }

        // Filter out lowercase categories (bad data) - only keep properly capitalized ones
        const validItems = this.data.data.items.filter((item) => {
            // Only keep 'Active' and 'Idle' with proper capitalization
            return item.category === 'Active' || item.category === 'Idle';
        });

        this.transformedData = validItems.map((item) => ({
            wellCategory: item.category,
            healthy: item.healthy,
            healthyWithMoc: item.healthyWithMoc,
            unhealthy: item.unhealthy,
            unhealthyWithMoc: item.unhealthyWithMoc,
            unhealthyWithHighRisk: item.unhealthyWithHighRisk,
        }));

        // Update ngx datatable if it's already initialized
        if (this.ngx?.dt) {
            this.ngx.dt.rows = this.transformedData;
            this.ngx.dt.page.totalRecords = this.transformedData.length;
            this.ngx.dt.page.totalPages = Math.ceil(
                this.transformedData.length / this.ngx.dt.page.size,
            );
        } else {
            // ngx datatable not yet initialized
        }
    }

    ngAfterViewInit() {
        // Set the initial data after view is initialized
        if (this.transformedData.length > 0) {
            this.ngx.dt.rows = this.transformedData;
            this.ngx.dt.page.totalRecords = this.transformedData.length;
            this.ngx.dt.page.totalPages = Math.ceil(
                this.transformedData.length / this.ngx.dt.page.size,
            );
        }
    }

    setDataTable() {
        return {
            column: {
                mode: ColumnMode.flex,
                header: [
                    {
                        key: 'wellCategory',
                        name: 'Well Category',
                        props: {
                            flexGrow: 0.5,
                            sortable: false,
                        },
                        render: (row: any) => row.wellCategory || '',
                    },
                    {
                        key: 'healthy',
                        name: 'Healthy',
                        props: {
                            flexGrow: 1,
                            sortable: false,
                        },
                        render: (row: any) => row.healthy ?? 0,
                    },
                    {
                        key: 'healthyWithMoc',
                        name: 'Healthy With MOC',
                        props: {
                            flexGrow: 1,
                            sortable: false,
                        },
                        render: (row: any) => row.healthyWithMoc ?? 0,
                    },
                    {
                        key: 'unhealthy',
                        name: 'Unhealthy',
                        props: {
                            flexGrow: 1,
                            sortable: false,
                        },
                        render: (row: any) => row.unhealthy ?? 0,
                    },
                    {
                        key: 'unhealthyWithMoc',
                        name: 'Unhealthy With MOC',
                        props: {
                            flexGrow: 1,
                            sortable: false,
                        },
                        render: (row: any) => row.unhealthyWithMoc ?? 0,
                    },
                    {
                        key: 'unhealthyWithHighRisk',
                        name: 'Unhealthy With High Risk',
                        props: {
                            flexGrow: 1,
                            sortable: false,
                        },
                        render: (row: any) => row.unhealthyWithHighRisk ?? 0,
                    },
                ],
            },
        };
    }
}
